// ---- Curved gable roof geometry ----
// Storybook / concept-B crown: a CLOSED low-poly roof volume that SEATS on the
// wall plate. Pitch runs ridge → wall top; the eave overhang is a short kicked
// lip past the walls (not a continuation of the full pitch — that left a huge
// open attic triangle above the walls). Camber arches the ridge; kick lifts
// only the outer eave tips.
//
// THREE-but-DOM-free — safe on the headless server world-build path.
// Geos are per-build (consolidate as "others"); not SHARED_GEOS.

import * as THREE from "three";

export type CurveRoofSpec = {
  ridgeAxis: "x" | "z";
  minX: number; maxX: number; minZ: number; maxZ: number;
  baseY: number;
  height: number;
  eave: number;
  /** Ridge arch amplitude (world units). 0 = straight ridge. */
  camber: number;
  /** Eave-tip upturn amplitude (world units). 0 = flat overhang. */
  kick: number;
  /** Samples along the pitch (ridge→eave tip). */
  pitchSegs: number;
  /** Samples along the ridge length. */
  alongSegs: number;
  /** Shell thickness (underside offset). */
  shellT: number;
};

export type CurveRoofPoint = { x: number; y: number; z: number };

export type CurveRoofBuilt = {
  shells: THREE.BufferGeometry[];
  unders: THREE.BufferGeometry[];
  gables: THREE.BufferGeometry[];
  eaveCaps: THREE.BufferGeometry[];
  /** Horizontal plate under the overhang (wall plate → eave tip). */
  plates: THREE.BufferGeometry[];
  /**
   * Solid fill under each roof half (baseY → soffit). Closes the long-side
   * view into the A-frame attic — gable walls only seal the front/back.
   */
  fills: THREE.BufferGeometry[];
  ridge: CurveRoofPoint[];
  eaveL: CurveRoofPoint[];
  eaveR: CurveRoofPoint[];
  barge0: CurveRoofPoint[];
  barge1: CurveRoofPoint[];
};

/**
 * Sample one half of the curved gable.
 * u: 0 = ridge, 1 = outer eave tip (past the wall).
 * Lateral run seats the pitch on the WALL PLATE (uWall), then a short overhang.
 */
function sampleHalf(
  u: number,
  v: number,
  sign: 1 | -1,
  ridgeAxis: "x" | "z",
  cx: number, cz: number,
  /** Half-extent of the mass walls (no eave). */
  wallH: number,
  /** Half-extent of the roof including eave. */
  roofH: number,
  /** Half-extent along the ridge (including eave). */
  roofL: number,
  baseY: number, height: number,
  camber: number, kick: number,
): CurveRoofPoint {
  const ridgeHalf = 0.04;
  // u at the outer wall face (where the roof must meet the wall plate).
  const uWall = Math.max(0.15, Math.min(0.92, (wallH - ridgeHalf) / (roofH - ridgeHalf)));
  const end = Math.pow(Math.abs(Math.cos(Math.PI * v)), 1.2);

  let y: number;
  if (u <= uWall) {
    // Main pitch: ridge (height) → wall plate (baseY). Soft belly + camber.
    const t = u / uWall; // 0 ridge → 1 wall
    const yStraight = baseY + height * (1 - t);
    const belly = -0.06 * height * Math.sin(Math.PI * t) * (0.55 + 0.45 * Math.sin(Math.PI * v));
    const camberY = camber * Math.sin(Math.PI * v) * Math.pow(1 - t, 1.2);
    y = yStraight + belly + camberY;
  } else {
    // Overhang past the wall: stay near baseY, kick up toward the tip (gable ends).
    const t = (u - uWall) / (1 - uWall); // 0 wall → 1 tip
    const kickY = kick * Math.pow(t, 1.4) * (0.3 + 0.7 * end);
    // Tiny drop just past the wall so the plate reads as a lip, then kick.
    y = baseY - 0.04 * Math.sin(Math.PI * Math.min(1, t * 1.2)) + kickY;
  }

  const halfAtU = ridgeHalf + (roofH - ridgeHalf) * u;
  // Mild outward flair only on the overhang.
  const flair = u > uWall
    ? kick * 0.2 * Math.pow((u - uWall) / (1 - uWall), 1.5) * (0.4 + 0.6 * end)
    : 0;

  if (ridgeAxis === "z") {
    return {
      x: cx + sign * (halfAtU + flair),
      y,
      z: (cz - roofL) + v * (roofL * 2),
    };
  }
  return {
    x: (cx - roofL) + v * (roofL * 2),
    y,
    z: cz + sign * (halfAtU + flair),
  };
}

function tri(
  positions: number[], indices: number[],
  a: CurveRoofPoint, b: CurveRoofPoint, c: CurveRoofPoint,
): void {
  const i = positions.length / 3;
  positions.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
  indices.push(i, i + 1, i + 2);
}

function gridSurface(
  nu: number, nv: number,
  at: (iu: number, iv: number) => CurveRoofPoint,
  outward: boolean,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let iv = 0; iv <= nv; iv++) {
    for (let iu = 0; iu <= nu; iu++) {
      const p = at(iu, iv);
      positions.push(p.x, p.y, p.z);
    }
  }
  const stride = nu + 1;
  for (let iv = 0; iv < nv; iv++) {
    for (let iu = 0; iu < nu; iu++) {
      const a = iv * stride + iu;
      const b = a + 1;
      const c = a + stride;
      const d = c + 1;
      if (outward) indices.push(a, b, d, a, d, c);
      else indices.push(a, d, b, a, c, d);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Vertical gable wall: filled A under the pitch at fixed v, down to baseY.
 * Profile runs ridge → **wall plate only** (not out to the eave tip). The
 * overhang past the wall is sealed by eave caps / plates / barge — extending
 * the gable under the eave put near-flat tris on y=baseY that z-fought the
 * ceiling, fill bottom, and wall tops.
 *
 * `outNudge` pushes the whole wall slightly outward along the ridge axis so it
 * never coplanes with attic-fill internals or the mass wall's end face.
 */
function gableWall(
  iuMax: number,
  at: (iu: number, sign: 1 | -1) => CurveRoofPoint,
  baseY: number,
  outwardSign: 1 | -1,
  ridgeAxis: "x" | "z",
  outNudge = 0.05,
): THREE.BufferGeometry {
  const profile: CurveRoofPoint[] = [];
  for (let iu = iuMax; iu >= 0; iu--) profile.push(at(iu, -1));
  for (let iu = 1; iu <= iuMax; iu++) profile.push(at(iu, 1));

  // Nudge every point outward so the face sits just outside coplanar rivals.
  const nudge = (p: CurveRoofPoint): CurveRoofPoint =>
    ridgeAxis === "z"
      ? { x: p.x, y: p.y, z: p.z + outwardSign * outNudge }
      : { x: p.x + outwardSign * outNudge, y: p.y, z: p.z };

  const top = profile.map(nudge);
  const basePts = top.map((p) => ({ x: p.x, y: baseY, z: p.z }));
  const positions: number[] = [];
  const indices: number[] = [];

  for (let i = 0; i < top.length - 1; i++) {
    const t0 = top[i]!, t1 = top[i + 1]!;
    const b0 = basePts[i]!, b1 = basePts[i + 1]!;
    // Skip near-degenerate strips (top already on the plate).
    if (Math.max(t0.y, t1.y) < baseY + 0.04) continue;
    if (outwardSign < 0) {
      tri(positions, indices, t0, t1, b1);
      tri(positions, indices, t0, b1, b0);
    } else {
      tri(positions, indices, t0, b1, t1);
      tri(positions, indices, t0, b0, b1);
    }
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Cap the eave lip: ribbon from outer surface to underside. */
function eaveCap(
  nv: number,
  outer: (iv: number) => CurveRoofPoint,
  under: (iv: number) => CurveRoofPoint,
  outward: boolean,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let iv = 0; iv <= nv; iv++) {
    const o = outer(iv), u = under(iv);
    positions.push(o.x, o.y, o.z, u.x, u.y, u.z);
  }
  for (let iv = 0; iv < nv; iv++) {
    const o0 = iv * 2, u0 = o0 + 1, o1 = o0 + 2, u1 = o0 + 3;
    if (outward) indices.push(o0, o1, u1, o0, u1, u0);
    else indices.push(o0, u1, o1, o0, u0, u1);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/**
 * Horizontal soffit plate under the overhang only (wall face → eave tip).
 * Seals the view when looking up from outside so the attic never reads open.
 * Built as a grid on the XY (or ZY) plane at ~baseY − shellT/2.
 */
function overhangPlate(
  nv: number,
  nOver: number,
  atWall: (iv: number) => CurveRoofPoint,
  atTip: (iv: number) => CurveRoofPoint,
  y: number,
  faceDown: boolean,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  // Rows along overhang (0 = wall, 1 = tip); cols along ridge (v).
  for (let io = 0; io <= nOver; io++) {
    const s = io / nOver;
    for (let iv = 0; iv <= nv; iv++) {
      const w = atWall(iv), t = atTip(iv);
      positions.push(
        w.x + (t.x - w.x) * s,
        y,
        w.z + (t.z - w.z) * s,
      );
    }
  }
  const stride = nv + 1;
  for (let io = 0; io < nOver; io++) {
    for (let iv = 0; iv < nv; iv++) {
      const a = io * stride + iv;
      const b = a + 1;
      const c = a + stride;
      const d = c + 1;
      // faceDown → normals −Y (visible looking up under the eave).
      // (a→b = +iv, a→c = +overhang; RH cross for faceDown needs opposite wind.)
      if (faceDown) indices.push(a, c, b, b, c, d);
      else indices.push(a, b, c, b, d, c);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

function path(n: number, at: (t: number) => CurveRoofPoint): CurveRoofPoint[] {
  const out: CurveRoofPoint[] = [];
  for (let i = 0; i <= n; i++) out.push(at(i / n));
  return out;
}

/**
 * Solid under one roof half: for every surface cell, a prism from baseY up to
 * the soffit. Looking in from the long eaves over the wall plate hits this fill
 * instead of open sky/attic (gable walls only close the front/back ends).
 *
 * Only the **eave face** (+ bottom) is emitted as a side wall. Skipped on purpose:
 *  - **Gable ends (iv)** — coplanar with `gableWall` → checkerboard on the A
 *  - **Ridge face (iu===0)** — L and R halves sit ~ridgeHalf apart with DoubleSide
 *    materials → a flickering dark vertical plane through the attic (often reads
 *    as a z-fight strip against the far plaster when looking in under the eaves)
 *
 * Bottom sits a hair **below** baseY so it doesn't coplane with the mass ceiling
 * top (both would land on y = wall plate).
 */
function halfFill(
  nu: number, nv: number,
  topAt: (iu: number, iv: number) => CurveRoofPoint,
  baseY: number,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  // Bottom inset — clears the ceiling slab that tops out at roof baseY.
  const botY = baseY - 0.05;
  // Two layers of verts: top (soffit) then bottom (below wall plate).
  for (let iv = 0; iv <= nv; iv++) {
    for (let iu = 0; iu <= nu; iu++) {
      const p = topAt(iu, iv);
      positions.push(p.x, p.y, p.z);
    }
  }
  const topCount = (nu + 1) * (nv + 1);
  for (let iv = 0; iv <= nv; iv++) {
    for (let iu = 0; iu <= nu; iu++) {
      const p = topAt(iu, iv);
      positions.push(p.x, botY, p.z);
    }
  }
  const stride = nu + 1;
  const bot = topCount;
  for (let iv = 0; iv < nv; iv++) {
    for (let iu = 0; iu < nu; iu++) {
      const t00 = iv * stride + iu;
      const t10 = t00 + 1;
      const t01 = t00 + stride;
      const t11 = t01 + 1;
      const b00 = bot + t00, b10 = bot + t10, b01 = bot + t01, b11 = bot + t11;
      // Bottom (face down) — every cell:
      indices.push(b00, b01, b10, b10, b01, b11);
      // Eave face only (+iu). No ridge, no gable ends (see header).
      if (iu === nu - 1) {
        indices.push(t10, t11, b11, t10, b11, b10);
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Build all curved-gable meshes + soft-trim sample paths. */
export function buildCurveRoof(spec: CurveRoofSpec): CurveRoofBuilt {
  const {
    ridgeAxis, minX, maxX, minZ, maxZ, baseY, height, eave,
    camber, kick, pitchSegs, alongSegs, shellT,
  } = spec;
  const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
  // Wall half-extents (mass footprint — no eave).
  const wallHX = (maxX - minX) / 2, wallHZ = (maxZ - minZ) / 2;
  // Roof half-extents including eave.
  const roofHX = wallHX + eave, roofHZ = wallHZ + eave;
  const nu = Math.max(5, pitchSegs);
  const nv = Math.max(4, alongSegs);

  // For ridge along z: lateral is X (wallHX/roofHX), length is Z (roofHZ).
  const wallH = ridgeAxis === "z" ? wallHX : wallHZ;
  const roofH = ridgeAxis === "z" ? roofHX : roofHZ;
  const roofL = ridgeAxis === "z" ? roofHZ : roofHX;

  const halfAt = (iu: number, iv: number, sign: 1 | -1) =>
    sampleHalf(
      iu / nu, iv / nv, sign, ridgeAxis, cx, cz,
      wallH, roofH, roofL, baseY, height, camber, kick,
    );

  // Top shells — sky-facing (+Y). Left half mirrors right winding.
  const shellL = gridSurface(nu, nv, (iu, iv) => halfAt(iu, iv, -1), true);
  const shellR = gridSurface(nu, nv, (iu, iv) => halfAt(iu, iv, 1), false);

  // Soffit — drop along −Y; normals face DOWN (looking up under eave).
  const underAt = (iu: number, iv: number, sign: 1 | -1): CurveRoofPoint => {
    const p = halfAt(iu, iv, sign);
    return { x: p.x, y: p.y - shellT, z: p.z };
  };
  const underL = gridSurface(nu, nv, (iu, iv) => underAt(iu, iv, -1), false);
  const underR = gridSurface(nu, nv, (iu, iv) => underAt(iu, iv, 1), true);

  // Overhang soffit plates — horizontal seal wall→tip under each eave.
  // (Computed before gables so the wall-plate iu index can bound the A-fill.)
  const uWall = Math.max(0.15, Math.min(0.92, (wallH - 0.04) / (roofH - 0.04)));
  const iuWall = Math.max(1, Math.round(uWall * nu));

  // Gable walls at both ends — solid A down to wall plate (ridge → wall only).
  const gableAt = (iu: number, sign: 1 | -1, v: number) =>
    sampleHalf(iu / nu, v, sign, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
  const gable0 = gableWall(iuWall, (iu, s) => gableAt(iu, s, 0), baseY, -1, ridgeAxis);
  const gable1 = gableWall(iuWall, (iu, s) => gableAt(iu, s, 1), baseY, 1, ridgeAxis);

  // Eave lip caps.
  const eaveCapL = eaveCap(nv, (iv) => halfAt(nu, iv, -1), (iv) => underAt(nu, iv, -1), true);
  const eaveCapR = eaveCap(nv, (iv) => halfAt(nu, iv, 1), (iv) => underAt(nu, iv, 1), true);
  const plateY = baseY - shellT * 0.55;
  // Left overhang runs −X as io increases; right runs +X — same triangle wind
  // yields opposite normals, so flip faceDown on the left half.
  const plateL = overhangPlate(
    nv, 3,
    (iv) => halfAt(iuWall, iv, -1),
    (iv) => halfAt(nu, iv, -1),
    plateY, false, // → −Y
  );
  const plateR = overhangPlate(
    nv, 3,
    (iv) => halfAt(iuWall, iv, 1),
    (iv) => halfAt(nu, iv, 1),
    plateY, true, // → −Y
  );

  // Attic fill under each half — seals the LONG-SIDE view into the A-frame
  // (gable walls only close front/back). Top of fill = soffit surface.
  const fillL = halfFill(nu, nv, (iu, iv) => underAt(iu, iv, -1), baseY);
  const fillR = halfFill(nu, nv, (iu, iv) => underAt(iu, iv, 1), baseY);

  // Soft-trim paths.
  const ridge = path(nv, (t) => {
    const p = sampleHalf(0, t, 1, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
    return {
      x: ridgeAxis === "z" ? cx : p.x,
      y: p.y + 0.14,
      z: ridgeAxis === "z" ? p.z : cz,
    };
  });
  const eaveL = path(nv, (t) => {
    const p = sampleHalf(1, t, -1, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
    return { x: p.x, y: p.y + 0.05, z: p.z };
  });
  const eaveR = path(nv, (t) => {
    const p = sampleHalf(1, t, 1, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
    return { x: p.x, y: p.y + 0.05, z: p.z };
  });
  const barge0 = path(nu * 2, (t) => {
    if (t <= 0.5) {
      const u = 1 - t * 2;
      const p = sampleHalf(u, 0, -1, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
      return { x: p.x, y: p.y + 0.07, z: p.z };
    }
    const u = (t - 0.5) * 2;
    const p = sampleHalf(u, 0, 1, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
    return { x: p.x, y: p.y + 0.07, z: p.z };
  });
  const barge1 = path(nu * 2, (t) => {
    if (t <= 0.5) {
      const u = 1 - t * 2;
      const p = sampleHalf(u, 1, -1, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
      return { x: p.x, y: p.y + 0.07, z: p.z };
    }
    const u = (t - 0.5) * 2;
    const p = sampleHalf(u, 1, 1, ridgeAxis, cx, cz, wallH, roofH, roofL, baseY, height, camber, kick);
    return { x: p.x, y: p.y + 0.07, z: p.z };
  });

  return {
    shells: [shellL, shellR],
    unders: [underL, underR],
    gables: [gable0, gable1],
    eaveCaps: [eaveCapL, eaveCapR],
    plates: [plateL, plateR],
    fills: [fillL, fillR],
    ridge, eaveL, eaveR, barge0, barge1,
  };
}

/** Polyline → rod segments for soft architectural trim. */
export function strokePath(
  pts: CurveRoofPoint[],
  radius: number,
  color: number,
  rod: (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, r: number, c: number) => void,
  ball?: (x: number, y: number, z: number, r: number, c: number) => void,
  endCapScale = 1.15,
): void {
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!, b = pts[i + 1]!;
    rod(a.x, a.y, a.z, b.x, b.y, b.z, radius, color);
  }
  if (ball && pts.length > 0) {
    const a = pts[0]!, b = pts[pts.length - 1]!;
    ball(a.x, a.y, a.z, radius * endCapScale, color);
    ball(b.x, b.y, b.z, radius * endCapScale, color);
  }
}
